import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Compass, Trophy, Medal, Crown } from "@phosphor-icons/react";
import api from "../api";

function SeasonList() {
  const [seasons, setSeasons] = useState([]);
  useEffect(() => { api.get("/seasons").then((r) => setSeasons(r.data.seasons)).catch(() => {}); }, []);
  return (
    <div className="space-y-4" data-testid="seasons-list">
      <div className="flex items-center gap-2">
        <Trophy size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Seasonal Archives</h1>
      </div>
      {seasons.length === 0 && <p className="text-ff-muted" data-testid="seasons-empty">No finalized seasons yet — the first campaign is still underway.</p>}
      {seasons.map((s) => (
        <Link key={s.season_id} to={`/seasons/${s.season_id}`} data-testid={`season-card-${s.season_id}`}
          className="ff-panel block rounded-md p-5 transition-colors hover:border-ff-gold">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-ff-parchment">{s.name}</h2>
            {s.corrected && <span className="rounded-full border border-ff-brass/30 px-2 py-0.5 text-[10px] uppercase text-ff-brass">Corrected</span>}
          </div>
          <p className="mt-1 text-sm text-ff-muted">
            {s.champion ? <>Champion: <span className="text-ff-gold">{s.champion.name}</span> · </> : null}
            {s.eligible_population} eligible explorers · scoring v{s.scoring_version}
          </p>
        </Link>
      ))}
    </div>
  );
}

function SeasonDetail() {
  const { seasonId } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/seasons/${seasonId}`).then((r) => setA(r.data)).catch(() => setErr(true)); }, [seasonId]);

  if (err) return <div className="ff-panel rounded-md p-8 text-center text-ff-muted" data-testid="season-detail-error">Season archive not found.</div>;
  if (!a) return <div className="ff-panel rounded-md p-8 text-center text-ff-muted">Loading archive…</div>;

  return (
    <div className="space-y-6" data-testid="season-detail">
      <Link to="/seasons" className="text-sm text-ff-brass hover:text-ff-gold">← All seasons</Link>
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{a.name}</h1>
        <p className="mt-1 text-sm text-ff-muted">{a.eligible_population} eligible explorers · scoring v{a.scoring_version} · finalized {String(a.finalized_at).slice(0, 10)}</p>
      </div>

      {a.podium?.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3" data-testid="season-podium">
          {a.podium.map((p) => (
            <div key={p.position} className="ff-panel rounded-md p-4 text-center">
              {p.position === 1 ? <Crown size={28} weight="duotone" className="mx-auto text-ff-gold" /> : <Medal size={24} weight="duotone" className="mx-auto text-ff-brass" />}
              <div className="mt-2 text-xs uppercase tracking-widest text-ff-muted">#{p.position}</div>
              <div className="font-display text-lg font-bold text-ff-parchment">{p.name}</div>
              <div className="text-xs text-ff-muted">{p.rank}</div>
            </div>
          ))}
        </div>
      )}

      <div className="ff-panel rounded-md p-5" data-testid="season-top100">
        <h2 className="text-lg font-semibold text-ff-gold">Final Top 100</h2>
        <div className="mt-3 divide-y divide-ff-brass/10">
          {(a.top_100 || []).map((r) => (
            <div key={r.position} className="flex items-center justify-between py-2 text-sm">
              <span className="text-ff-muted">#{r.position}</span>
              <span className="flex-1 px-3 text-ff-parchment">{r.name}{r.public_title ? <span className="ml-2 text-xs text-ff-brass">{r.public_title}</span> : null}{r.prestige_tier ? <span className="ml-2 text-[11px] text-ff-gold/70">· {r.prestige_tier}</span> : null}</span>
              <span className="text-ff-gold">{r.frontier_score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Seasons({ detail }) {
  return (
    <div className="min-h-screen bg-ff-bg text-ff-parchment">
      <header className="border-b border-ff-brass/25 bg-ff-bg/90">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <Compass size={26} weight="duotone" className="text-ff-gold animate-compass" />
            <span className="font-display text-lg font-bold text-ff-gold">Aurumward</span>
          </Link>
          <Link to="/login" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted hover:border-ff-gold hover:text-ff-gold">Enter</Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-10">{detail ? <SeasonDetail /> : <SeasonList />}</main>
    </div>
  );
}
